/* eslint-disable no-useless-escape */
import { createElement, Fragment, ReactElement } from 'react'

import { Helmet } from 'react-helmet-async'

import { Page, PageSeoTagType } from '@tribeplatform/gql-client/types'
import { useRouterPath } from '@tribeplatform/react-sdk'

type PageMetaProps = {
  page: Page
}

export const PageMeta = ({ page }: PageMetaProps) => {
  const pathname = useRouterPath()
  const seoDetails = page?.seoDetail
  const jsonld = seoDetails?.jsonld
  const validatedJsonld = validateJsonld(jsonld)

  return (
    <Helmet
      bodyAttributes={{
        'data-path': pathname?.replace(/[^\_\-a-zA-Z0-9\/]/g, ''),
        'data-matched-route': `/${page?.space?.address?.path || ''}`,
      }}
      script={[
        ...(validatedJsonld
          ? [
              {
                type: 'application/ld+json',
                innerHTML: validatedJsonld,
              },
            ]
          : []),
      ]}
    >
      {...getSeoMetaTags(seoDetails)}
    </Helmet>
  )
}

type TGetSeoMeta = (args: {
  title: string
  description?: string
  jsonld?: string
  additionalMeta?: Page['seoDetail']['additionalMeta']
}) => Array<ReactElement>

export const getSeoMetaTags: TGetSeoMeta = seoDetails => {
  if (!seoDetails) {
    return []
  }

  const { additionalMeta, title, description } = seoDetails
  const additionalMetaHasTitle = additionalMeta?.some(
    tag => tag.type === PageSeoTagType.TITLE,
  )
  const additionalMetaHasDescription = additionalMeta?.some(
    tag =>
      tag.type === PageSeoTagType.META &&
      tag.properties?.some(
        property => property.key === 'name' && property.value === 'description',
      ),
  )

  return [
    !additionalMetaHasTitle ? (
      <title
        key="title"
        // Adding data-rh to signal react-helmet-async that this tag is managed by react-helmet-async
        data-rh="true"
      >
        {title}
      </title>
    ) : (
      <Fragment key="title"></Fragment>
    ),
    description && !additionalMetaHasDescription ? (
      <meta
        // Adding data-rh to signal react-helmet-async that this tag is managed by react-helmet-async
        data-rh="true"
        key="description"
        name="description"
        content={description}
      />
    ) : (
      <Fragment key="description"></Fragment>
    ),
    ...(additionalMeta?.map((tag, index) => {
      try {
        return createElement(tag.type.toLowerCase().trim(), {
          ...tag.properties.reduce((props, keyValue) => {
            return {
              ...props,
              [keyValue.key]: keyValue.value,
              // Adding data-rh to signal react-helmet-async that this tag is managed by react-helmet-async
              'data-rh': 'true',
            }
          }, {}),
          key: index,
        })
      } catch (error) {
        logger.error('error parsing additional meta tags', { tag, error })
      }
      return null
    }) ?? []),
  ]
}

const validateJsonld = (jsonld?: string): string | undefined => {
  try {
    if (typeof jsonld === 'string') {
      return JSON.stringify(JSON.parse(jsonld))
    }

    return undefined
  } catch (e) {
    return undefined
  }
}
